import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

import type { SearchSelectOption } from './SearchSelect';

// ==============================|| SHARED — PER-COLUMN LIST FILTERS [STD-FE] ||====================== //
//
// The state half of a filterable list. The controls themselves are rendered by `DataTable`
// (@zercode/ui) into a second header row, one control under the column it filters; a page declares
// each filter ONCE, on the column, and passes the same array to both this hook and the grid.
//
// This replaced a separate filter bar above the grid. Per-column filters cost one 36px row instead of
// a whole panel, and — the real reason — a control sitting under its own column header needs no label
// and cannot drift from the thing it filters.
//
// Two guarantees carried over from the bar, because both are defects that already shipped here:
//
//   1. A FILTER CHANGE RESETS THE PAGE. Filter while standing on page 4 of 4 and the narrowed result
//      has one page, so the request asks for rows 31-40 of 7, the grid renders empty, and it reads as
//      "the filter returned nothing". Eighteen admin pages each got this right by remembering to write
//      `setPage(0)` next to every setter — eighteen chances to forget, and one forgotten setter is
//      invisible until an admin filters from a deep page. `setValue` is the only way to change a
//      filter, so the reset is structural rather than remembered.
//
//   2. A TEXT FILTER MUST BE DEBOUNCED. Undebounced, every keystroke is a list request and the
//      responses can land out of order — the grid settles on the answer to "26M" while the box reads
//      "26MY3". `commerce/Orders.tsx` learned this and grew a private `useDebounced`;
//      `operations/Tickets.tsx` still fires one query per keystroke. Here `values` is what the input
//      shows (instant) and `query` is what the request may use (settled), so a page that wires its
//      queryKey to the thing shaped like query params gets the debounce whether it knew to want it.

interface ColumnFilterCommon {
  /**
   * The value key, and therefore the query-parameter name the page sends. Defaults to the column's
   * `field`.
   *
   * Set it when the column and the parameter are not the same word: an `email` column narrowed by an
   * `email` parameter needs nothing, but a `roles` column narrowed by `roleType` does. Keeping the two
   * separable is what stops renaming a column silently changing the request.
   */
  param?: string;
}

/** One filter control, rendered under its column's header. */
export type ColumnFilter = ColumnFilterCommon &
  (
    | { kind: 'text'; placeholder?: string }
    /** Short, closed vocabulary (status, type, priority). Gets an "all" row at the top. */
    | { kind: 'select'; options: ColumnFilterOption[]; allLabel?: string }
    /**
     * Several options at once from the same closed vocabulary — "status is Pending OR Approved".
     *
     * ⚠ THE VALUE IS STILL ONE STRING: the chosen options comma-joined, `''` when nothing is checked.
     * That is not a shortcut — it is what lets every other part of the list stay untouched. `values`,
     * `query`, the queryKey, `activeCount`, `clearAll` and the export all speak {@link FilterValues},
     * so widening this one filter to `string[]` would widen the state type for all 43 list pages to buy
     * nothing the join does not already give. The wire shape is `?param=a,b`, which is the one this
     * platform already uses for a list of ids (`?ids=`, `?ageCategoryIds=`).
     *
     * ⚠ THE ENDPOINT MUST OR THEM, AND MOST OF OURS DO NOT. The comma-joined lists the backend takes
     * today are AND-ed (`ageCategoryIds` emits one EXISTS per id) or resolve a set of ids. A column
     * filtered by this kind is asking a question no endpoint answers until it grows a parameter that
     * ORs, and the FE cannot tell the difference — it gets rows back either way. Check the handler
     * before reaching for this.
     *
     * ⚠ DEBOUNCED LIKE A TEXT FILTER, because a checkbox list is typed-at speed too: three ticks is
     * three list requests without it, and the responses can land out of order. `values` shows the ticks
     * instantly; `query` settles.
     *
     * `allLabel` is the RESTING CAPTION ("Tüm Durumlar"), not a row — the way to stop filtering is to
     * untick, so an "all" row would be a second, contradictory control for the same thing.
     */
    | { kind: 'multiSelect'; options: ColumnFilterOption[]; allLabel?: string }
    /**
     * Open catalogue (country, school, company, user). Type-to-filter, because ~250 countries in a
     * native menu offer no way in but scrolling — and because `SearchSelect` carries the
     * duplicate-label key fix that a catalogue of PEOPLE needs.
     */
    | { kind: 'searchSelect'; options: SearchSelectOption[]; loading?: boolean }
    | { kind: 'date' }
    /** Two-state. Checked sends `'true'`; unchecked sends nothing. For three states use `select`. */
    | { kind: 'toggle' }
    /**
     * A control the PAGE renders, for a catalogue too large to hand over as an array.
     *
     * ⚠ THIS IS NOT A GENERAL ESCAPE HATCH, AND THE DISTINCTION IS SHARP. Use it when the options
     * cannot be an array at all — the school register is 11,779 rows and the user directory grows with
     * the tenant, so both need a SERVER-SEARCHED picker that owns its own query, debounce, label cache
     * and "load more" footer. Reproducing any of that inside `DataTable` would fork it from
     * `SchoolSelect`, which already solved it (SCH-PICK) and is the component a page hands over here.
     *
     * For anything with a bounded option list, use `select` (closed vocabulary) or `searchSelect`
     * (catalogue you can fetch whole) — a filter declared as a node is invisible to any future sweep
     * over filter kinds, which is the cost of this door.
     *
     * The contract is the same as every other kind: `value` is a string, `''` means "not filtering",
     * and the control MUST call `onChange` rather than owning the value. `ariaLabel` is supplied
     * because a filter control has no visible label — it is identified by the header above it, which a
     * screen reader cannot see.
     */
    | { kind: 'node'; render: (control: ColumnFilterControl) => ReactNode }
  );

/** What a `node` filter is handed. See that variant's note. */
export interface ColumnFilterControl {
  /** The current filter value; `''` when nothing is chosen. */
  value: string;
  /** Call with the new value, or `''` to clear. Resets the page, like every other filter. */
  onChange: (next: string) => void;
  /** Accessible name for the control, already localized by the grid's `toolbarLabels.filterFor`. */
  ariaLabel: string;
}

/**
 * The value key a column's filter reads and writes.
 *
 * ⚠ EXPORTED SO THE HOOK AND THE GRID CANNOT DISAGREE. The state lives in @zercode/forms and the
 * controls in @zercode/ui; if each computed this itself, a `param` override would land in one and not
 * the other, and the control would write to a key the request never reads — a filter that types fine
 * and narrows nothing.
 */
export function filterKey(column: FilterableColumn): string {
  return column.filter?.param ?? column.field;
}

export interface ColumnFilterOption {
  value: string;
  label: string;
}

/**
 * USR-SORT: which column the list is ordered by, and which way. `null` = the server's own default.
 *
 * Deliberately NOT MUI's `GridSortModel`: this package holds the state and @zercode/ui holds the grid,
 * and an array-of-one whose shape is owned by a third-party grid would put a library type in a page's
 * state. `DataTable` converts at its own edge.
 */
export interface ColumnSort {
  /** The column's `field` — NOT the wire token. See {@link sortKey}. */
  field: string;
  descending: boolean;
}

/**
 * The wire token for a column's sort, defaulting to its `field`.
 *
 * ⚠ THE TWIN OF {@link filterKey}, AND FOR THE SAME REASON. A column called `createdAt` is sorted by
 * `?sort=created`, `displayName` by `?sort=name`, `teacherUserId` by `?sort=teacher` — the server names
 * a COLUMN, not a DTO field, and the whitelist that validates it lives in another language. Keeping the
 * two separable is what stops renaming a column silently changing the request; keeping the function
 * exported is what stops the hook and the grid disagreeing about the answer.
 */
export function sortKey(column: SortableColumn): string {
  return column.sortParam ?? column.field;
}

/** Every filter value is a string; `''` means "not filtering on this". Keeps serialization uniform. */
export type FilterValues = Record<string, string>;

/** The shape this hook reads off a column. `DataTableColDef` satisfies it. */
export interface FilterableColumn {
  field: string;
  filter?: ColumnFilter;
  /** USR-SORT: the `?sort=` token when it differs from `field`. See {@link sortKey}. */
  sortParam?: string;
  /** USR-SORT: whether this column offers ordering at all — mirrors MUI's own `sortable`. */
  sortable?: boolean;
}

/** The half of a column {@link sortKey} reads. */
export interface SortableColumn {
  field: string;
  sortParam?: string;
}

/** Default settle delay for debounced filters. 300ms is what Orders.tsx arrived at in the field. */
export const FILTER_DEBOUNCE_MS = 300;

/**
 * The kinds whose value settles before it reaches `query`.
 *
 * `text` because a word is typed a letter at a time; `multiSelect` because a set is built a tick at a
 * time, and the two are the same problem — several changes a human means as ONE, each of which would
 * otherwise be a list request, with responses free to land out of order. The single-valued kinds are
 * not here on purpose: picking a status is one deliberate act and delaying it would only feel slow.
 */
const DEBOUNCED_KINDS = new Set<ColumnFilter['kind']>(['text', 'multiSelect']);

export interface UseColumnFiltersOptions {
  /** Rows per page before the user changes it. Matches DataTable's first `pageSizeOptions` entry. */
  initialPageSize?: number;
  /** Settle delay for `text` filters. Override only with a reason; 0 disables debouncing entirely. */
  debounceMs?: number;
  /** Seeds `values` — e.g. a status this page always opens on. Absent fields default to ''. */
  initialValues?: FilterValues;
  /**
   * USR-SORT: the order the page opens on. Omit for the server's own default, which is what every list
   * here has today — do NOT mirror the server's default in here, or the page sends a sort the server
   * would have applied anyway and buys a second cache key for the same answer.
   */
  initialSort?: ColumnSort;
}

export interface ColumnFiltersState {
  /** Instant values — pass to `<DataTable filterValues>`. NOT for the queryKey (see note 2 above). */
  values: FilterValues;
  /** Settled, empty entries dropped. Put THIS in the queryKey and send it as query params. */
  query: FilterValues;
  /** The only way to change a filter, and therefore the only place the page reset can be forgotten. */
  setValue: (field: string, value: string) => void;
  clearAll: () => void;
  /** LIST-VIEW: replace every filter at once (restoring a saved view). REPLACES, never merges. */
  setAll: (next: FilterValues) => void;
  /** How many filters the user has set — for the grid's count chip. */
  activeCount: number;
  /** 0-based, DataGrid's convention; the API is 1-based [STD-API-8]. */
  page: number;
  pageSize: number;
  /** Wire straight to `<DataTable onPaginationChange>`. */
  setPagination: (page: number, pageSize: number) => void;

  // ------------------------------------------------------------------ USR-SORT
  /** Current ordering, or null for the server's default. Pass to `<DataTable sort>`. */
  sort: ColumnSort | null;
  /**
   * The only way to change the ordering — and therefore, like `setValue`, the only place the page reset
   * could be forgotten. Comes straight from `<DataTable onSortChange>`.
   */
  setSort: (next: ColumnSort | null) => void;
  /**
   * `{ sort, order }` ready to spread into the request, `{}` when unsorted. Put it in the queryKey
   * beside `query`: without it, re-sorting would serve the previously cached page and read as a grid
   * that ignores its own header.
   */
  sortQuery: { sort?: string; order?: string };
}

/**
 * Filter + pagination state for one list page, keyed by column field.
 *
 * ⚠ THE POINT OF THE HOOK IS THAT `setValue` RESETS THE PAGE. If a page ever needs to set a filter
 * without that, it needs a different hook and a comment saying why — not a second `useState`.
 */
export function useColumnFilters(columns: readonly FilterableColumn[], options: UseColumnFiltersOptions = {}): ColumnFiltersState {
  const { initialPageSize = 10, debounceMs = FILTER_DEBOUNCE_MS, initialValues, initialSort } = options;

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [sort, setSortState] = useState<ColumnSort | null>(initialSort ?? null);

  // ⚠ EVERY DEPENDENCY BELOW IS A PRIMITIVE, ON PURPOSE. A page builds its column array inline, so the
  // array — and anything memoised on it — has a fresh identity on every render. An effect depending on
  // such a value would clear and restart its timer every render, the timeout would never fire, and the
  // settled value would never arrive: the list would simply never filter on text. Serializing to a
  // string first makes the dependency change only when the CONTENT changes.
  const fieldsKey = JSON.stringify(columns.filter((c) => c.filter).map((c) => [filterKey(c), c.filter!.kind]));
  const entries = useMemo(() => JSON.parse(fieldsKey) as [string, ColumnFilter['kind']][], [fieldsKey]);

  const [values, setValues] = useState<FilterValues>(() =>
    Object.fromEntries(entries.map(([field]) => [field, initialValues?.[field] ?? '']))
  );

  const debouncedFields = useMemo(
    () => new Set(entries.filter(([, kind]) => DEBOUNCED_KINDS.has(kind)).map(([field]) => field)),
    [entries]
  );

  const debouncedSignature = JSON.stringify([...debouncedFields].map((field) => [field, (values[field] ?? '').trim()]));
  const rawDebounced = useMemo(() => Object.fromEntries(JSON.parse(debouncedSignature) as [string, string][]), [debouncedSignature]);

  const [settledDebounced, setSettledDebounced] = useState<FilterValues>(() =>
    Object.fromEntries(entries.filter(([, k]) => DEBOUNCED_KINDS.has(k)).map(([f]) => [f, (initialValues?.[f] ?? '').trim()]))
  );

  useEffect(() => {
    // `debounceMs: 0` is handled by DERIVATION below, not here. Writing state synchronously in an
    // effect is the cascading-render pattern react-hooks/set-state-in-effect exists to stop, and
    // routing it through `setTimeout(_, 0)` instead would make "no debounce" still cost a tick.
    if (debounceMs <= 0) return;
    const timer = setTimeout(() => setSettledDebounced(rawDebounced), debounceMs);
    return () => clearTimeout(timer);
  }, [rawDebounced, debounceMs]);

  const effectiveDebounced = debounceMs <= 0 ? rawDebounced : settledDebounced;

  const setValue = useCallback((field: string, value: string) => {
    setValues((prev) => (prev[field] === value ? prev : { ...prev, [field]: value }));
    setPage(0); // note 1 — never remove; a narrowed list has fewer pages than the one we are standing on
  }, []);

  const clearAll = useCallback(() => {
    setValues((prev) => Object.fromEntries(Object.keys(prev).map((field) => [field, ''])));
    setPage(0);
  }, []);

  /**
   * LIST-VIEW: replace every filter at once — restoring a saved view, and nothing else.
   *
   * ⚠ REPLACE, NOT MERGE, and that is what makes a view mean something. Merging would leave whatever
   * the reader had already narrowed sitting underneath the view they just opened, so the same view
   * would show different rows depending on what came before it.
   *
   * ⚠ FIELDS THE VIEW DOES NOT MENTION ARE CLEARED, not left alone — same reason. And a key in the
   * view that this page no longer HAS is dropped on the floor here: a saved view outlives the page it
   * was saved against, so an unknown filter must degrade quietly rather than send a parameter the
   * endpoint would refuse.
   *
   * ⚠ RESETS THE PAGE for the reason every other setter does (note 1 at the top of this file): the
   * restored filter set has its own number of pages and standing on page 4 of the old one reads as an
   * empty result.
   */
  const setAll = useCallback(
    (next: FilterValues) => {
      setValues((prev) =>
        Object.fromEntries(Object.keys(prev).map((field) => [field, next[field] ?? '']))
      );
      setPage(0);
    },
    []
  );

  // Empty entries are DROPPED rather than sent as ''. An empty string is a value the backend has to
  // decide about; absence is not. It also keeps the queryKey stable, so clearing a filter returns to the
  // cached unfiltered page instead of minting a new key for the same question.
  const query = useMemo(() => {
    const out: FilterValues = {};
    for (const field of Object.keys(values)) {
      const raw = debouncedFields.has(field) ? (effectiveDebounced[field] ?? '') : values[field];
      if (raw !== undefined && raw !== '') out[field] = raw;
    }
    return out;
  }, [values, effectiveDebounced, debouncedFields]);

  // ⚠ COUNTED OVER EVERY FILTERABLE COLUMN, INCLUDING ONES THE USER HAS HIDDEN. Hiding a column hides
  // its control but does NOT drop its filter — dropping it would silently widen the result set behind
  // the admin's back. So the count is the only remaining evidence that the list is narrowed, and it has
  // to keep counting a filter whose box is off screen. Counted from `values` rather than `query`
  // because the chip must agree with the boxes, and during the debounce window those two disagree.
  const activeCount = useMemo(() => entries.filter(([field]) => (values[field] ?? '') !== '').length, [entries, values]);

  const setPagination = useCallback((nextPage: number, nextPageSize: number) => {
    setPage(nextPage);
    setPageSize(nextPageSize);
  }, []);

  /**
   * ⚠ RE-SORTING RESETS THE PAGE, for exactly the reason a filter change does (note 1 at the top of
   * this file). Standing on page 4 and clicking a header asks the server for rows 31-40 of a DIFFERENTLY
   * ORDERED set: the rows that come back are strangers, the admin did not ask to move, and nothing says
   * the page changed under them. Unlike a filter it cannot even be explained by "the result got
   * smaller" — the total is identical, which makes it read as the sort having shuffled the list at
   * random.
   */
  const setSort = useCallback((next: ColumnSort | null) => {
    setSortState(next);
    setPage(0);
  }, []);

  // The column array is rebuilt every render, so the field -> token map is derived from the serialized
  // signature rather than the array itself — the same rule every other dependency in this hook follows.
  const sortParamsKey = JSON.stringify(columns.map((c) => [c.field, sortKey(c)]));
  const sortQuery = useMemo(() => {
    if (!sort) return {};
    const params = new Map(JSON.parse(sortParamsKey) as [string, string][]);
    return { sort: params.get(sort.field) ?? sort.field, order: sort.descending ? 'desc' : 'asc' };
  }, [sort, sortParamsKey]);

  return {
    values, query, setValue, clearAll, setAll, activeCount, page, pageSize, setPagination,
    sort, setSort, sortQuery
  };
}
